/**
 * Design tool → simulator handoff: the key figures of a design (PV power, inverter power, roof orientations,
 * battery) packed into the simulator URL fragment (`/mo-phong/#thiet-ke=<base64url JSON>`).
 * The fragment never reaches a server. Values are validated on both sides; messages are Vietnamese.
 */
import { z } from 'zod';

export const SIM_HANDOFF_KEY = 'thiet-ke';
export const SIM_HANDOFF_VERSION = 1;

const plane = z.object({
  /** Roof index on the drawing (0-based) */
  roof: z.number().int().min(0).max(500),
  panels: z.number().int().min(1).max(100_000),
  /** Compass bearing the panels face: 0 = North, 90 = East, 180 = South, 270 = West */
  azimuthDeg: z.number().min(0).lt(360),
  tiltDeg: z.number().min(0).max(90),
});

export const simHandoffSchema = z.object({
  v: z.literal(SIM_HANDOFF_VERSION),
  /** Design code / name, shown in the simulator notice */
  code: z.string().max(40).nullable().default(null),
  name: z.string().max(200).nullable().default(null),
  panelCount: z.number().int().min(1).max(100_000),
  moduleWp: z.number().positive().max(2000),
  pvKwp: z.number().positive().max(1500),
  /** Total inverter AC rating (kW); null when no inverter was selected */
  inverterKw: z.number().positive().max(1000).nullable().default(null),
  inverterLabel: z.string().max(250).nullable().default(null),
  /** Module Pmax temperature coefficient (%/°C) from the datasheet, when known */
  tempCoeffPmaxPct: z.number().min(-2).max(0).nullable().default(null),
  /** Total battery energy (kWh); null = no battery */
  batteryKwh: z.number().positive().max(1024).nullable().default(null),
  planes: z.array(plane).min(1).max(200),
});
export type SimHandoff = z.infer<typeof simHandoffSchema>;
export type SimHandoffPlane = z.infer<typeof plane>;

const norm360 = (d: number) => ((d % 360) + 360) % 360;

/**
 * Builds the handoff from the layout: panels grouped by roof, each roof with its orientation.
 * Returns null when the layout has no panels.
 */
export function buildSimHandoff(input: {
  code?: string | null;
  name?: string | null;
  /** Roof index of every placed panel */
  panelRoofs: number[];
  /** Orientation of each roof (index = roof index) */
  roofOrientation: (roof: number) => { faceDeg: number; pitchDeg: number };
  moduleWp: number;
  inverterKw?: number | null;
  inverterLabel?: string | null;
  tempCoeffPmaxPct?: number | null;
  batteryKwh?: number | null;
}): SimHandoff | null {
  if (!input.panelRoofs.length || !(input.moduleWp > 0)) return null;
  const count = new Map<number, number>();
  for (const r of input.panelRoofs) count.set(r, (count.get(r) ?? 0) + 1);
  const planes = [...count.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([roof, panels]) => {
      const o = input.roofOrientation(roof);
      return {
        roof,
        panels,
        azimuthDeg: (Math.round(norm360(o.faceDeg) * 10) / 10) % 360,
        tiltDeg: Math.min(90, Math.max(0, Math.round(o.pitchDeg * 10) / 10)),
      };
    });
  const panelCount = input.panelRoofs.length;
  return simHandoffSchema.parse({
    v: SIM_HANDOFF_VERSION,
    code: input.code ?? null,
    name: input.name?.slice(0, 200) ?? null,
    panelCount,
    moduleWp: input.moduleWp,
    pvKwp: Math.round(panelCount * input.moduleWp) / 1000,
    inverterKw: input.inverterKw && input.inverterKw > 0 ? Math.round(input.inverterKw * 1000) / 1000 : null,
    inverterLabel: input.inverterLabel?.slice(0, 250) ?? null,
    tempCoeffPmaxPct: input.tempCoeffPmaxPct ?? null,
    batteryKwh: input.batteryKwh && input.batteryKwh > 0 ? Math.round(input.batteryKwh * 100) / 100 : null,
    planes,
  });
}

/** Plane with the most panels (ties: lower roof index) — the simulator models a single plane. */
export function dominantPlane(h: SimHandoff): SimHandoffPlane {
  return [...h.planes].sort((a, b) => b.panels - a.panels || a.roof - b.roof)[0]!;
}

/** True when the roofs face different directions / tilts (single-plane simulation is an approximation). */
export function hasMixedOrientation(h: SimHandoff): boolean {
  const d = dominantPlane(h);
  return h.planes.some(
    (p) =>
      Math.abs(((p.azimuthDeg - d.azimuthDeg + 540) % 360) - 180) > 5 || Math.abs(p.tiltDeg - d.tiltDeg) > 2,
  );
}

// Base64url + UTF-8 without platform APIs (works in browsers and Node; shared has no DOM typings).
const ALPHA = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
const utf8 = (s: string): number[] =>
  encodeURIComponent(s)
    .split(/(%[0-9A-F]{2})/i)
    .flatMap((part) =>
      part.startsWith('%') && part.length === 3
        ? [parseInt(part.slice(1), 16)]
        : [...part].map((c) => c.charCodeAt(0)),
    );
const fromUtf8 = (bytes: number[]) =>
  decodeURIComponent(bytes.map((b) => `%${b.toString(16).padStart(2, '0')}`).join(''));
function toB64Url(bytes: number[]): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const n = (bytes[i]! << 16) | ((bytes[i + 1] ?? 0) << 8) | (bytes[i + 2] ?? 0);
    const chars = [(n >> 18) & 63, (n >> 12) & 63, (n >> 6) & 63, n & 63].map((k) => ALPHA[k]);
    out += chars.slice(0, Math.min(4, bytes.length - i + 1)).join('');
  }
  return out;
}
function fromB64Url(s: string): number[] {
  if (!/^[A-Za-z0-9_-]*$/.test(s) || s.length % 4 === 1) throw new Error('base64url');
  const bytes: number[] = [];
  for (let i = 0; i < s.length; i += 4) {
    const v = [...s.slice(i, i + 4)].map((c) => ALPHA.indexOf(c));
    const n = (v[0]! << 18) | (v[1]! << 12) | ((v[2] ?? 0) << 6) | (v[3] ?? 0);
    bytes.push((n >> 16) & 255);
    if (v.length > 2) bytes.push((n >> 8) & 255);
    if (v.length > 3) bytes.push(n & 255);
  }
  return bytes;
}

export function encodeSimHandoff(h: SimHandoff): string {
  return toB64Url(utf8(JSON.stringify(simHandoffSchema.parse(h))));
}

/** URL fragment for the simulator (without the leading "#"). */
export function simHandoffFragment(h: SimHandoff): string {
  return `${SIM_HANDOFF_KEY}=${encodeSimHandoff(h)}`;
}

export function decodeSimHandoff(
  encoded: string,
): { ok: true; data: SimHandoff } | { ok: false; error: string } {
  if (encoded.length > 20_000) return { ok: false, error: 'Dữ liệu thiết kế quá lớn.' };
  let raw: unknown;
  try {
    raw = JSON.parse(fromUtf8(fromB64Url(encoded)));
  } catch {
    return { ok: false, error: 'Dữ liệu thiết kế không đọc được (đường dẫn bị hỏng).' };
  }
  const r = simHandoffSchema.safeParse(raw);
  if (!r.success)
    return {
      ok: false,
      error: `Dữ liệu thiết kế không hợp lệ: ${r.error.issues[0]?.path.join('.')} ${r.error.issues[0]?.message}`,
    };
  return { ok: true, data: r.data };
}

/** Reads the handoff from a URL hash ("#thiet-ke=…"); null when absent. */
export function readSimHandoffFromHash(hash: string) {
  const m = new RegExp(`(?:^#?|&)${SIM_HANDOFF_KEY}=([A-Za-z0-9_-]+)`).exec(hash);
  return m ? decodeSimHandoff(m[1]!) : null;
}
